import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { namespace } from "@common-config/constants";
import {
  selectCreditPackages,
  selectError,
  selectIsLoading,
  selectSelectedPackageId,
  setSelectedPackage
} from '@common-reducers/CreditPurchaseReducer';
import {
  CreditPackage,
  fetchCreditPackagesThunk,
  purchaseCreditPackageThunk
} from '@common-reducers/CreditPurchaseThunk';
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { activeSubscriptionSelector } from '@common-reducers/UserSelectors';
import { useGetItemPricesMutation, useGetOwnedSubscriptionItemIdsMutation } from '@common-reducers/backend-api/backend-api';
import { Check, Info, AlertTriangle, Coins, Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from "react-i18next";
import { selectCreditsData, selectUsageData, fetchCreditUsage, selectIsLoading as selectCreditUsageIsLoading } from '@common-reducers/CreditUsageReducer';
import { backendApiEndpoints } from '@common-reducers/backend-api/backend-api';
import {
  buildUnlimitedCreditsCheckoutItems,
  getUnlimitedCreditsAddonState,
  planCycleFromPlanId,
  unlimitedCreditsPrice,
} from './unlimited-credits-addon';
import { AnalyticsService } from '@common-services/analytics-service';
import { modifyPlanName } from '@common-services/utils';
import { computeDailyPace } from './credits-forecast';
import { isHeavyBurner, monthlyTopUpCost, nextPlanUpgrade, packLastsDays, recommendPackCredits } from './buy-modal-advice';
import { AddCreditsSource, buildCreditsContext, buildPackProps, CreditsEventProps, useCreditsEvents } from './credits-events';

interface AddCreditsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** #357: which surface opened the modal (analytics only). */
  source?: AddCreditsSource;
}

type BillingCycle = 'monthly' | 'annual';

// Define subscription plan types
interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  features: string[];
  monthly: {
    price: number;
    credits: number;
    planId: string;
  };
  annual: {
    price: number;
    credits: number;
    savePercentage: number;
    planId: string;
  };
  popular?: boolean;
}

// Static plan metadata (features, descriptions, etc.) - prices will be fetched from API
const SUBSCRIPTION_PLAN_METADATA = [
  {
    id: 'scheduler',
    name: 'Basic',
    chargebeeType: 'Basic',
    description: 'Perfect for getting started',
    features: ['Basic messaging', 'Schedule messages', 'Basic automation'],
    credits: 250,
  },
  {
    id: 'manager',
    name: 'Standard',
    chargebeeType: 'StandardNew',
    description: 'Best for growing businesses',
    features: ['Everything in Basic', 'Advanced bots', 'Analytics', 'Priority support'],
    credits: 500,
    popular: true,
  },
  {
    id: 'campaigner',
    name: 'Pro',
    chargebeeType: 'Pro',
    description: 'For power users and teams',
    features: ['Everything in Standard', 'Team collaboration', 'Advanced integrations', 'Custom features'],
    credits: 1000,
  },
];

const AddCreditsModal: React.FC<AddCreditsModalProps> = ({ open, onOpenChange, source = 'unknown' }) => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const trackCreditsEvent = useCreditsEvents();

  // Get active subscription info
  const activeSubscription = useAppSelector(activeSubscriptionSelector);
  // Customer-facing plan name: the raw value is the Chargebee item id ("Pro-USD-Monthly").
  const activePlanLabel = modifyPlanName(activeSubscription?.planName).replace('StandardNew', 'Standard');
  // Get current credit usage data
  const creditsData = useAppSelector(selectCreditsData);
  const creditUsageIsLoading = useAppSelector(selectCreditUsageIsLoading);

  // #357 funnel context (plan/tier/cycle + usage band). Held in a ref so the click handlers and the
  // open-effect below read the latest values without re-firing when the usage payload refreshes.
  const creditsContextRef = useRef<CreditsEventProps>({});
  creditsContextRef.current = buildCreditsContext({
    isOwner: true, // the modal only mounts for the workspace owner (credits-usage.tsx)
    planId: activeSubscription?.plan_id,
    creditsData,
  });
  const wasOpenRef = useRef(false);
  // Once the owner picks a pack themselves we stop steering the selection.
  const userPickedPackRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      userPickedPackRef.current = false;
      trackCreditsEvent('add_credits_open', { ...creditsContextRef.current, source });
    }
    wasOpenRef.current = open;
  }, [open, source, trackCreditsEvent]);

  // Fetch real pricing data from API
  const [getItemPrices, { isLoading: isLoadingPrices, data: itemPricesData }] = useGetItemPricesMutation();

  // Owned subscription item ids — used to tell whether the "Unlimited Auto-Replies"
  // recurring add-on is already active (so we don't offer to buy it twice).
  const [getOwnedItemIds, { data: ownedItemsData }] = useGetOwnedSubscriptionItemIdsMutation();
  const [isAddonLoading, setIsAddonLoading] = useState(false);

  // Exact cycle of the plan item price (null = unknown plan → the Unlimited Credits card is not offered).
  const unlimitedCreditsCycle = planCycleFromPlanId(activeSubscription?.plan_id);

  // Determine subscription billing cycle
  const subscriptionBillingCycle = useMemo((): BillingCycle | null => {
    if (!activeSubscription) return null;

    // Check subscription plan_id or other properties to determine billing cycle
    // This will depend on how your subscription data is structured
    const planId = activeSubscription.plan_id?.toLowerCase() || '';
    if (planId.includes('annual') || planId.includes('yearly')) {
      return 'annual';
    } else if (planId.includes('monthly')) {
      return 'monthly';
    }

    // Default to monthly if can't determine
    return 'monthly';
  }, [activeSubscription]);

  // State for billing cycle - initialize with subscription billing cycle if available
  const [billingCycle, setBillingCycle] = useState<BillingCycle>(subscriptionBillingCycle || 'monthly');

  // Fetch pricing data when modal opens
  useEffect(() => {
    if (open) {
      getItemPrices().unwrap()
        .then(response => console.log('Item prices response:', response))
        .catch(error => console.error('Error fetching item prices:', error));
      // Owned items → is the Unlimited Auto-Replies add-on already active?
      getOwnedItemIds().unwrap()
        .catch(error => console.error('Error fetching owned subscription items:', error));
    }
  }, [getItemPrices, getOwnedItemIds, open]);

  // Create dynamic subscription plans from API data
  const SUBSCRIPTION_PLANS = useMemo(() => {
    if (isLoadingPrices || !itemPricesData) {
      // Return plans with loading state or fallback prices
      return SUBSCRIPTION_PLAN_METADATA.map(plan => ({
        ...plan,
        monthly: { price: 0, credits: plan.credits, planId: plan.id },
        annual: { price: 0, credits: plan.credits, savePercentage: 10, planId: plan.id },
      }));
    }

    return SUBSCRIPTION_PLAN_METADATA.map(plan => {
      // Find monthly and annual pricing from API data
      const monthlyPrice = ((itemPricesData as unknown) as Array<{
        id: string;
        tiers: Array<{ starting_unit: number; ending_unit?: number; price: number; }>;
      }>).find(price => price.id === `${plan.chargebeeType}-USD-Monthly`);

      const annualPrice = ((itemPricesData as unknown) as Array<{
        id: string;
        tiers: Array<{ starting_unit: number; ending_unit?: number; price: number; }>;
      }>).find(price => price.id === `${plan.chargebeeType}-USD-Yearly`);

      const monthlyTier = monthlyPrice?.tiers?.[0];
      const annualTier = annualPrice?.tiers?.[0];

      const monthlyPriceValue = monthlyTier ? monthlyTier.price / 100 : 0;
      const annualPriceValue = annualTier ? annualTier.price / 100 : 0;
      const annualMonthlyPrice = annualPriceValue / 12;
      const savePercentage = monthlyPriceValue > 0 ? Math.round(((monthlyPriceValue - annualMonthlyPrice) / monthlyPriceValue) * 100) : 0;

      return {
        ...plan,
        monthly: {
          price: monthlyPriceValue,
          credits: plan.credits,
          planId: plan.id
        },
        annual: {
          price: annualPriceValue,
          credits: plan.credits,
          savePercentage,
          planId: plan.id
        },
      };
    });
  }, [itemPricesData, isLoadingPrices]);

  // State for selected subscription plan (for users without subscription)
  const [selectedPlanId, setSelectedPlanId] = useState<string>('manager'); // Default to manager, will be updated when plans load
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);

  // Update selected plan when subscription plans load
  useEffect(() => {
    const popularPlan = SUBSCRIPTION_PLANS.find(p => p.popular);
    const defaultPlan = popularPlan || SUBSCRIPTION_PLANS[0];
    if (defaultPlan && selectedPlanId === 'manager' && SUBSCRIPTION_PLANS.length > 0) {
      setSelectedPlanId(defaultPlan.id);
    }
  }, [SUBSCRIPTION_PLANS, selectedPlanId]);

  // Update billing cycle if subscription changes
  useEffect(() => {
    if (subscriptionBillingCycle) {
      setBillingCycle(subscriptionBillingCycle);
    }
  }, [subscriptionBillingCycle]);

  // Use Redux state with fallbacks for safety
  const creditPackages = useAppSelector(selectCreditPackages);
  const selectedPackageId = useAppSelector(selectSelectedPackageId);
  // #357 change 6: every number below is the WORKSPACE's (the usage series and creditsData are aggregated
  // by workspaceId on the backend; credits are one pool the owner tops up for the team).
  const usageData = useAppSelector(selectUsageData);
  const workspacePerDay = useMemo(() => computeDailyPace({ usageData }), [usageData]);
  const recommendedCredits = useMemo(() => recommendPackCredits({
    packCredits: creditPackages.filter(p => p.oneTime).map(p => p.credits),
    perDay: workspacePerDay,
    creditsRemaining: creditsData?.creditsRemaining ?? 0,
    daysToRenewal: creditsData?.daysRemaining ?? 0,
  }), [creditPackages, workspacePerDay, creditsData]);
  const isLoading = useAppSelector(selectIsLoading) || false;
  const error = useAppSelector(selectError);

  // Find selected package and billing cycle details
  const selectedPackage = creditPackages.find(pkg => {
    const monthlyId = pkg.monthly?.id;
    const annualId = pkg.annual?.id;
    return (billingCycle === 'monthly' && monthlyId === selectedPackageId) ||
      (billingCycle === 'annual' && annualId === selectedPackageId);
  });

  // Fetch credit packages and usage data when the modal opens
  useEffect(() => {
    if (open) {
      dispatch(fetchCreditPackagesThunk());

      // Also fetch credit usage data if not loaded or stale
      const isCreditDataEmpty = !creditsData ||
        creditsData.totalCredits === 0 &&
        creditsData.creditsUsed === 0 &&
        creditsData.creditsRemaining === 0;

      if (isCreditDataEmpty) {
        // Default to 30 days timeframe
        dispatch(fetchCreditUsage('30days'));
      }
    }
  }, [dispatch, open, creditsData]);

  // When packages load or billing cycle changes, select the first package
  useEffect(() => {
    if (creditPackages.length > 0) {
      // Try to find the current package in the new billing cycle
      let matchingPackage: CreditPackage | undefined;

      // Pre-select the pack sized to the workspace forecast until the owner picks one.
      if (!userPickedPackRef.current && recommendedCredits !== null) {
        matchingPackage = creditPackages.find(pkg => pkg.oneTime && pkg.credits === recommendedCredits);
      }

      if (!matchingPackage && selectedPackage) {
        // If we already have a selected package, try to find the same credit amount
        matchingPackage = creditPackages.find(pkg => pkg.credits === selectedPackage.credits);
      }

      // If no matching package or no previously selected package, prioritize the popular one
      if (!matchingPackage) {
        // First try to find the package with 200 credits (medium) which should be popular
        matchingPackage = creditPackages.find(pkg => pkg.credits === 200);

        // Then look for a package marked as popular
        if (!matchingPackage) {
          matchingPackage = creditPackages.find(pkg => pkg.popular);
        }

        // Fallback to the first package if nothing else works
        if (!matchingPackage) {
          matchingPackage = creditPackages[0];
        }
      }

      // Select the appropriate ID based on billing cycle
      if (matchingPackage) {
        const packageId = billingCycle === 'monthly'
          ? matchingPackage.monthly?.id
          : matchingPackage.annual?.id;

        if (packageId) {
          dispatch(setSelectedPackage(packageId));
        }
      }
    }
  }, [creditPackages, billingCycle, dispatch, selectedPackage, recommendedCredits]);

  // Handle package selection
  const handleSelectPackage = useCallback((pkg: CreditPackage) => {
    const packageId = billingCycle === 'monthly'
      ? pkg.monthly?.id
      : pkg.annual?.id;

    if (packageId) {
      userPickedPackRef.current = true;
      dispatch(setSelectedPackage(packageId));
      const pricing = billingCycle === 'monthly' ? pkg.monthly : pkg.annual;
      if (pricing) trackCreditsEvent('pack_select', { ...creditsContextRef.current, ...buildPackProps(pkg, pricing, billingCycle) });
    }
  }, [dispatch, billingCycle, trackCreditsEvent]);

  // Callback for continuing to checkout
  const handleContinueToCheckout = useCallback(async () => {
    if (!selectedPackageId) return;

    const pkg = selectedPackage;
    const pricing = pkg ? (billingCycle === 'monthly' ? pkg.monthly : pkg.annual) : undefined;
    trackCreditsEvent('checkout_start', {
      ...creditsContextRef.current,
      ...(pkg && pricing ? buildPackProps(pkg, pricing, billingCycle) : { packId: selectedPackageId, billingCycle }),
    });

    try {
      // Dispatch the purchase thunk
      const result = await dispatch(purchaseCreditPackageThunk({
        packageId: selectedPackageId,
        quantity: 1 // Default to 1, could be adjustable in the future
      })).unwrap();

      // If successful, redirect to the checkout URL
      if (result && result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
    } catch (err) {
      console.error('Error during checkout:', err);
    }
  }, [selectedPackageId, selectedPackage, billingCycle, trackCreditsEvent, dispatch]);

  // "Unlimited Credits" recurring add-on (#342): same merge_items checkout as the auto-replies add-on;
  // the price id follows the subscription's billing cycle (an add-on must match the plan period).
  const handleAddUnlimitedCredits = useCallback(async () => {
    if (isAddonLoading || !unlimitedCreditsCycle) return;
    setIsAddonLoading(true);
    trackCreditsEvent('addon_select', { ...creditsContextRef.current, addon: 'unlimited_credits' });
    trackCreditsEvent('checkout_start', { ...creditsContextRef.current, addon: 'unlimited_credits', withAddon: true });
    try {
      const analyticsService = new AnalyticsService();
      const freshSessionId = await analyticsService.getGA4SessionId();
      const gaClientId = await analyticsService.getGA4ClientId();

      const result = await dispatch(
        backendApiEndpoints.getCheckoutPageUrl.initiate({
          subscription_items: buildUnlimitedCreditsCheckoutItems(unlimitedCreditsCycle),
          merge_items: true,
          ...(freshSessionId && { ga_session_id: freshSessionId }),
          ...(gaClientId && { ga_client_id: gaClientId }),
        })
      ).unwrap();

      if (result && result.checkoutPageUrl) {
        window.location.href = result.checkoutPageUrl;
      } else {
        setIsAddonLoading(false);
      }
    } catch (err) {
      console.error('Error adding Unlimited Credits add-on:', err);
      setIsAddonLoading(false);
    }
  }, [dispatch, isAddonLoading, unlimitedCreditsCycle, trackCreditsEvent]);

  // Callback for subscription plan checkout (for users without subscription)
  const handleSubscriptionCheckout = useCallback(async () => {
    if (!selectedPlanId || isSubscriptionLoading) return;

    setIsSubscriptionLoading(true);
    trackCreditsEvent('checkout_start', {
      ...creditsContextRef.current,
      checkoutKind: 'plan',
      targetPlan: selectedPlanId,
      billingCycle,
      withPack: Boolean(selectedPackageId),
    });

    try {
      // Find the selected plan and get the correct item_price_id for Chargebee
      const selectedPlan = SUBSCRIPTION_PLANS.find(plan => plan.id === selectedPlanId);
      if (!selectedPlan) return;

      // Find the selected plan and get its Chargebee type
      const chosenPlan = SUBSCRIPTION_PLANS.find(plan => plan.id === selectedPlanId);
      if (!chosenPlan) return;

      const chargebeeType = chosenPlan.chargebeeType || 'StandardNew';
      const billingPeriod = billingCycle === 'monthly' ? 'Monthly' : 'Yearly';
      const chargebeePlanId = `${chargebeeType}-USD-${billingPeriod}`;

      // Prepare subscription items array
      const subscriptionItems = [
        {
          item_price_id: chargebeePlanId,
          quantity: 1
        }
      ];

      // Add credit add-on if selected
      if (selectedPackageId) {
        subscriptionItems.push({
          item_price_id: selectedPackageId,
          quantity: 1
        });
        console.log('Creating checkout for plan + credits:', chargebeePlanId, selectedPackageId);
      } else {
        console.log('Creating checkout for plan only:', chargebeePlanId);
      }

      // Fetch fresh GA4 session data for server-side purchase event linking
      const analyticsService = new AnalyticsService();
      const freshSessionId = await analyticsService.getGA4SessionId();
      const gaClientId = await analyticsService.getGA4ClientId();

      // Use the backend API to get checkout page URL with multiple items
      const result = await dispatch(
        backendApiEndpoints.getCheckoutPageUrl.initiate({
          subscription_items: subscriptionItems,
          ...(freshSessionId && { ga_session_id: freshSessionId }),
          ...(gaClientId && { ga_client_id: gaClientId }),
        })
      ).unwrap();

      // If successful, redirect to the checkout URL
      if (result && result.checkoutPageUrl) {
        window.location.href = result.checkoutPageUrl;
      }
    } catch (err) {
      console.error('Error during subscription checkout:', err);
      setIsSubscriptionLoading(false);
    }
  }, [selectedPlanId, billingCycle, selectedPackageId, dispatch, isSubscriptionLoading, trackCreditsEvent]);

  // Discount calculation for annual billing
  const getAnnualDiscount = (): number => {
    // Calculate average discount across packages
    if (creditPackages.length === 0) return 15; // Default 15% if no packages

    const packagesWithBothCycles = creditPackages.filter(
      pkg => pkg.monthly && pkg.annual
    );

    if (packagesWithBothCycles.length === 0) return 15;

    const totalDiscount = packagesWithBothCycles.reduce((sum, pkg) => {
      if (!pkg.monthly || !pkg.annual) return sum;

      const monthlyAnnualized = pkg.monthly.price * 12;
      const annualPrice = pkg.annual.price;
      const discount = ((monthlyAnnualized - annualPrice) / monthlyAnnualized) * 100;

      return sum + discount;
    }, 0);

    return Math.round(totalDiscount / packagesWithBothCycles.length);
  };

  const annualDiscount = getAnnualDiscount();

  // Check if user has an active subscription
  const hasActiveSubscription = Boolean(activeSubscription);

  const unlimitedCreditsAddon = getUnlimitedCreditsAddonState({
    hasActiveSubscription,
    ownedItemPriceIds: ownedItemsData?.itemPriceIds,
    cycle: unlimitedCreditsCycle,
  });

  // #357 change 6: top-up vs upgrade value-per-dollar, and the heavy-burner gate for Unlimited.
  const topUpPacks = creditPackages.filter(p => p.oneTime && p.monthly);
  const cheapestPerCredit = topUpPacks.length ? Math.min(...topUpPacks.map(p => p.monthly!.perCredit)) : 0;
  const workspaceMonthlyTopUpCost = monthlyTopUpCost(workspacePerDay, cheapestPerCredit);
  // Unlimited ($99) is only worth showing to workspaces whose month of top-ups would cost most of it
  // (or that already have it, so they can see it is active).
  const showUnlimitedCard = unlimitedCreditsAddon.show &&
    (unlimitedCreditsAddon.isActive || isHeavyBurner(workspaceMonthlyTopUpCost));
  const currentPlanMeta = SUBSCRIPTION_PLAN_METADATA.find(
    m => activeSubscription?.plan_id?.replace(/-USD-(Monthly|Yearly)$/, '') === m.chargebeeType,
  );
  const monthlyEquivalent = (plan: SubscriptionPlan) =>
    subscriptionBillingCycle === 'annual' ? plan.annual.price / 12 : plan.monthly.price;
  const currentPlanPriced = SUBSCRIPTION_PLANS.find(pl => pl.id === currentPlanMeta?.id);
  const upgradeOption = hasActiveSubscription && currentPlanPriced ? nextPlanUpgrade({
    currentCredits: currentPlanPriced.monthly.credits,
    currentPrice: monthlyEquivalent(currentPlanPriced),
    plans: SUBSCRIPTION_PLANS.map(pl => ({ name: pl.name, credits: pl.monthly.credits, price: monthlyEquivalent(pl) })),
  }) : null;
  const selectedTopUpPerCredit = (selectedPackage?.oneTime && selectedPackage.monthly?.perCredit) || cheapestPerCredit;

  // Check if billing cycle matches subscription billing cycle
  const billingCycleMatchesSubscription = !subscriptionBillingCycle || billingCycle === subscriptionBillingCycle;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[480px] p-4 z-[9500]"
        style={{
          backgroundColor: 'var(--add-credits-modal-bg)',
          borderColor: 'var(--add-credits-modal-border)',
          boxShadow: 'var(--add-credits-modal-shadow)'
        }}
      >
        <style>{`
          [data-radix-popper-content-wrapper] {
            z-index: 1500 !important; 
          }
          
          [data-slot="dialog-overlay"] {
            z-index: 1400 !important;
          }
          
          @keyframes shimmer {
            0% {
              background-position: -468px 0;
            }
            100% {
              background-position: 468px 0;
            }
          }
          
          .shimmer {
            background: linear-gradient(to right, 
              var(--add-credits-modal-shimmer-bg) 8%, 
              var(--add-credits-modal-shimmer-bg) 18%, 
              var(--add-credits-modal-shimmer-bg) 33%);
            background-size: 800px 104px;
            animation: shimmer 1.5s infinite linear;
          }
          
          /* Hide billing cycle heading */
          .hide-billing-cycle {
            display: block;
            margin: 12px 0 8px;
            text-align: center;
            width: 100%;
            position: relative;
            z-index: 5;
          }
          
          .hide-billing-cycle::before {
            content: "";
            position: absolute;
            left: 0;
            right: 0;
            height: 20px;
            background: var(--add-credits-modal-bg);
            z-index: -1;
            top: -5px;
          }
        `}</style>
        <DialogHeader className="pb-1">
          <DialogTitle
            className="text-xl font-semibold"
            style={{ color: 'var(--add-credits-modal-title)' }}
          >
            {t('credits.addCredits', 'Add Credits')}
          </DialogTitle>
          <DialogDescription
            className="text-xs"
            style={{ color: 'var(--add-credits-modal-description)' }}
          >
            {t('credits.choosePackageDescription', 'Choose a credit package to add to your account. Credits will be available immediately after purchase.')}
          </DialogDescription>
        </DialogHeader>

        {/* Current Subscription and Credits Information */}
        {hasActiveSubscription && (
          <div className="mb-3 mt-1">
            <Card
              className="p-3"
              style={{
                backgroundColor: 'var(--add-credits-modal-subscription-bg)',
                borderColor: 'var(--add-credits-modal-subscription-border)'
              }}
            >
              <CardContent className="p-3">
                <div className="space-y-2 text-sm">
                  <div className="flex items-start border-b border-[var(--color-border)] pb-1.5">
                    <span
                      className="font-medium whitespace-nowrap mr-1 mt-0.5"
                      style={{ color: 'var(--add-credits-modal-subscription-title)' }}
                    >
                      {t('credits.currentSubscription', 'Current Subscription')}:
                    </span>
                    <span
                      className="font-medium text-right flex-1 break-words"
                      style={{ color: 'var(--add-credits-modal-subscription-value)' }}
                    >
                      {activePlanLabel || t('credits.unavailable', 'Unavailable')}
                      {subscriptionBillingCycle && (
                        <span
                          className="ml-1 whitespace-nowrap"
                          style={{ color: 'var(--add-credits-modal-subscription-label)' }}
                        >
                          ({subscriptionBillingCycle === 'monthly' ?
                            t('credits.monthlyLower', 'monthly') :
                            t('credits.annualLower', 'annual')})
                        </span>
                      )}
                    </span>
                  </div>
                  {creditUsageIsLoading ? (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--add-credits-modal-subscription-label)' }}>
                          {t('credits.totalCredits', 'Total Credits')}:
                        </span>
                        <span className="w-10 h-4 shimmer rounded"></span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--add-credits-modal-subscription-label)' }}>
                          {t('credits.usedCredits', 'Used Credits')}:
                        </span>
                        <span className="w-10 h-4 shimmer rounded"></span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--add-credits-modal-subscription-label)' }}>
                          {t('credits.remainingCredits', 'Remaining')}:
                        </span>
                        <span className="w-10 h-4 shimmer rounded"></span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--add-credits-modal-subscription-label)' }}>
                          {t('credits.renewal', 'Renewal')}:
                        </span>
                        <span className="w-16 h-4 shimmer rounded"></span>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--add-credits-modal-subscription-label)' }}>
                          {t('credits.totalCredits', 'Total Credits')}:
                        </span>
                        <span
                          className="font-medium"
                          style={{ color: 'var(--add-credits-modal-subscription-title)' }}
                        >
                          {creditsData.totalCredits}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--add-credits-modal-subscription-label)' }}>
                          {t('credits.usedCredits', 'Used Credits')}:
                        </span>
                        <span
                          className="font-medium"
                          style={{ color: 'var(--add-credits-modal-subscription-title)' }}
                        >
                          {creditsData.creditsUsed}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--add-credits-modal-subscription-label)' }}>
                          {t('credits.remainingCredits', 'Remaining')}:
                        </span>
                        <span
                          className="font-medium"
                          style={{ color: 'var(--add-credits-modal-subscription-value)' }}
                        >
                          {creditsData.creditsRemaining}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--add-credits-modal-subscription-label)' }}>
                          {t('credits.renewal', 'Renewal')}:
                        </span>
                        <span
                          className="font-medium"
                          style={{ color: 'var(--add-credits-modal-subscription-title)' }}
                        >
                          {creditsData.renewalDate ?
                            creditsData.renewalDate :
                            t('credits.notAvailable', 'N/A')}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Credit recommendation for low credit users */}
                  {!creditUsageIsLoading && (
                    <>
                      {creditsData.creditsRemaining === 0 && (
                        <div
                          className="mt-1 pt-1 border-t text-xs flex items-center"
                          style={{
                            borderTopColor: 'var(--add-credits-modal-subscription-border)',
                            color: 'var(--add-credits-modal-alert-warning-text)'
                          }}
                        >
                          <AlertTriangle className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span>{t('credits.noCreditsRemaining', 'You have no credits remaining. Purchase credits to continue using premium features.')}</span>
                        </div>
                      )}
                      {creditsData.creditsRemaining > 0 && creditsData.percentageUsed > 90 && (
                        <div
                          className="mt-1 pt-1 border-t text-xs flex items-center"
                          style={{
                            borderTopColor: 'var(--add-credits-modal-subscription-border)',
                            color: 'var(--add-credits-modal-alert-warning-text)'
                          }}
                        >
                          <AlertTriangle className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span>{t('credits.lowCreditsRemaining', 'You have very few credits remaining. Consider purchasing additional credits soon.')}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recommendation for users without active subscription */}
        {!hasActiveSubscription && (
          <Alert
            className="my-1 py-2"
            style={{
              backgroundColor: 'var(--add-credits-modal-alert-info-bg)',
              borderColor: 'var(--add-credits-modal-alert-info-border)'
            }}
          >
            <Info
              className="h-4 w-4"
              style={{ color: 'var(--add-credits-modal-alert-info-text)' }}
            />
            <AlertDescription
              className="text-xs"
              style={{ color: 'var(--add-credits-modal-alert-info-text)' }}
            >
              {selectedPackageId ? (
                t('credits.subscriptionWithCreditsNote', 'Great! You can purchase both your subscription plan and credit add-ons together in one checkout.')
              ) : (
                t('credits.noActiveSubscription', 'Select a subscription plan above and optionally choose credit packages to purchase together.')
              )}
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert
            className="my-1 py-1.5"
            style={{
              backgroundColor: 'var(--add-credits-modal-alert-error-bg)',
              borderColor: 'var(--add-credits-modal-alert-error-border)'
            }}
          >
            <AlertDescription
              className="text-xs"
              style={{ color: 'var(--add-credits-modal-alert-error-text)' }}
            >
              {error}
            </AlertDescription>
          </Alert>
        )}

        {/* Billing cycle mismatch warning */}
        {hasActiveSubscription && subscriptionBillingCycle && !billingCycleMatchesSubscription && (
          <Alert
            className="my-1 py-1.5"
            style={{
              backgroundColor: 'var(--add-credits-modal-alert-warning-bg)',
              borderColor: 'var(--add-credits-modal-alert-warning-border)'
            }}
          >
            <AlertTriangle
              className="h-4 w-4"
              style={{ color: 'var(--add-credits-modal-alert-warning-text)' }}
            />
            <AlertDescription
              className="text-xs"
              style={{ color: 'var(--add-credits-modal-alert-warning-text)' }}
            >
              {t('credits.billingCycleMismatch', 'The selected billing cycle must match your subscription billing cycle. Your current subscription is {{cycle}}.',
                { cycle: subscriptionBillingCycle === 'monthly' ? 'monthly' : 'annual' })}
            </AlertDescription>
          </Alert>
        )}

        {/* Billing cycle toggle - only show if no subscription or if subscription doesn't specify a cycle */}
        {(!hasActiveSubscription || !subscriptionBillingCycle) && (
          <div className="flex justify-center my-3">
            <div
              className="inline-flex items-center p-0.5 rounded-lg text-sm"
              style={{ backgroundColor: 'var(--add-credits-modal-billing-toggle-bg)' }}
            >
              <button
                className={`px-4 py-1.5 rounded-md transition-colors ${billingCycle === 'monthly'
                    ? 'shadow-sm font-medium'
                    : 'hover:bg-opacity-80'
                  }`}
                style={{
                  backgroundColor: billingCycle === 'monthly'
                    ? 'var(--add-credits-modal-billing-toggle-active-bg)'
                    : 'transparent',
                  color: billingCycle === 'monthly'
                    ? 'var(--add-credits-modal-billing-toggle-active-text)'
                    : 'var(--add-credits-modal-billing-toggle-inactive-text)'
                }}
                onClick={() => setBillingCycle('monthly')}
              >
                {t('credits.monthly', 'Monthly')}
              </button>
              <button
                className={`px-4 py-1.5 rounded-md transition-colors ${billingCycle === 'annual'
                    ? 'shadow-sm font-medium'
                    : 'hover:bg-opacity-80'
                  }`}
                style={{
                  backgroundColor: billingCycle === 'annual'
                    ? 'var(--add-credits-modal-billing-toggle-active-bg)'
                    : 'transparent',
                  color: billingCycle === 'annual'
                    ? 'var(--add-credits-modal-billing-toggle-active-text)'
                    : 'var(--add-credits-modal-billing-toggle-inactive-text)'
                }}
                onClick={() => setBillingCycle('annual')}
              >
                {t('credits.annual', 'Annual')} ({annualDiscount}% {t('credits.off', 'off')})
              </button>
            </div>
          </div>
        )}

        {/* Subscription Plans Section - Compact Pills */}
        {!hasActiveSubscription ? (
          <div className="my-3">
            <div
              className="text-sm font-medium mb-2 text-center"
              style={{ color: 'var(--add-credits-modal-section-title)' }}
            >
              {t('credits.chooseSubscriptionPlan', 'Choose your subscription plan')}
            </div>
            {isLoadingPrices ? (
              <div className="flex justify-center items-center h-32">
                <p style={{ color: 'var(--add-credits-modal-loading-text)' }}>
                  {t('credits.loading', 'Loading plans...')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2 mt-4">
                {SUBSCRIPTION_PLANS.map((plan) => {
                  const pricing = billingCycle === 'monthly' ? plan.monthly : plan.annual;
                  const isSelected = selectedPlanId === plan.id;
                  const isPopular = plan.popular;

                  return (
                    <div key={plan.id} className="relative">
                      <Card
                        className={`
                        h-[75px] cursor-pointer relative border-2 transition-all duration-200 p-2
                        ${isSelected ? 'shadow-sm' : 'hover:shadow-sm'}
                      `}
                        style={{
                          backgroundColor: isSelected
                            ? 'var(--add-credits-modal-plan-card-selected-bg)'
                            : 'var(--add-credits-modal-plan-card-bg)',
                          borderColor: isSelected
                            ? 'var(--add-credits-modal-plan-card-selected-border)'
                            : 'var(--add-credits-modal-plan-card-border)'
                        }}
                        onClick={() => setSelectedPlanId(plan.id)}
                      >
                        {isPopular && (
                          <div
                            className="absolute -top-3 left-1/2 transform -translate-x-1/2 text-xs px-1.5 py-0.5 rounded font-medium z-10 whitespace-nowrap"
                            style={{
                              backgroundColor: 'var(--add-credits-modal-popular-badge-bg)',
                              color: 'var(--add-credits-modal-popular-badge-text)'
                            }}
                          >
                            ⭐ {t('credits.popular', 'Popular')}
                          </div>
                        )}

                        <CardContent className="p-0 h-full flex flex-col justify-between">
                          {/* Plan name and price */}
                          <div className="text-center">
                            <div
                              className="text-sm font-semibold"
                              style={{ color: 'var(--add-credits-modal-plan-name)' }}
                            >
                              {plan.name}
                            </div>
                            <div
                              className="text-xs"
                              style={{ color: 'var(--add-credits-modal-plan-credits)' }}
                            >
                              {pricing.credits} credits
                            </div>
                          </div>

                          {/* Price */}
                          <div className="text-center">
                            <div
                              className="text-lg font-bold whitespace-nowrap"
                              style={{ color: 'var(--add-credits-modal-plan-price)' }}
                            >
                              ${pricing.price.toFixed(2)}
                              <span
                                className="text-xs font-normal ml-0.5"
                                style={{ color: 'var(--add-credits-modal-plan-period)' }}
                              >
                                /{billingCycle === 'monthly' ? t('credits.month', 'mo') : t('credits.year', 'yr')}
                              </span>
                            </div>
                          </div>

                          {isSelected && (
                            <div
                              className="absolute top-1 right-1"
                              style={{ color: 'var(--add-credits-modal-plan-card-selected-border)' }}
                            >
                              <Check className="h-3 w-3" />
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          /* Minimal Subscription Display for users with active subscription */
          hasActiveSubscription && (
            <div
              className="flex items-center justify-between py-2 px-3 rounded-lg mb-4"
              style={{
                backgroundColor: 'var(--add-credits-modal-subscription-bg)',
                borderColor: 'var(--add-credits-modal-subscription-border)',
                border: '1px solid'
              }}
            >
              <div className="flex items-center gap-2">
                <Check
                  className="h-4 w-4"
                  style={{ color: 'var(--add-credits-modal-subscription-value)' }}
                />
                <span
                  className="text-sm font-medium"
                  style={{ color: 'var(--add-credits-modal-subscription-title)' }}
                >
                  {activePlanLabel || t('credits.currentPlan', 'Current Plan')}
                </span>
                <span
                  className="text-xs"
                  style={{ color: 'var(--add-credits-modal-subscription-label)' }}
                >
                  ({subscriptionBillingCycle === 'monthly' ?
                    t('credits.monthly', 'Monthly') :
                    t('credits.annual', 'Annual')})
                </span>
              </div>
              <span
                className="text-xs font-medium"
                style={{ color: 'var(--add-credits-modal-subscription-value)' }}
              >
                {t('credits.active', 'Active')}
              </span>
            </div>
          )
        )}

        {/* NOTE: There should be no "Current billing cycle: Monthly" text here. 
            If it appears, it might be coming from a hard-coded string or translation in a parent component. */}

        {/* Unlimited Credits recurring add-on ($129/mo, #342/#357): AI credits are never blocked while it is
            active. Same shape as the auto-replies card; only for active subscribers. */}
        {showUnlimitedCard && (
          <div className="mt-2 mb-1">
            <Card
              className="p-3"
              style={{
                backgroundColor: 'var(--add-credits-modal-subscription-bg)',
                borderColor: unlimitedCreditsAddon.isActive
                  ? 'var(--add-credits-modal-package-card-selected-border)'
                  : 'var(--add-credits-modal-subscription-border)',
                border: '1px solid'
              }}
              data-testid="unlimited-credits-addon-card"
            >
              <CardContent className="p-0">
                <div className="flex items-start gap-3">
                  <Coins
                    className="h-5 w-5 mt-0.5 flex-shrink-0"
                    style={{ color: 'var(--add-credits-modal-subscription-value)' }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className="text-sm font-semibold"
                        style={{ color: 'var(--add-credits-modal-subscription-title)' }}
                      >
                        {t('credits.unlimitedCreditsAddonTitle', '5,000 credits / month')}
                      </span>
                      <span
                        className="text-sm font-bold whitespace-nowrap"
                        style={{ color: 'var(--add-credits-modal-plan-price)' }}
                      >
                        {`$${unlimitedCreditsPrice(unlimitedCreditsCycle ?? 'monthly').toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        <span
                          className="text-xs font-normal ml-0.5"
                          style={{ color: 'var(--add-credits-modal-plan-period)' }}
                        >
                          {unlimitedCreditsCycle === 'annual'
                            ? t('credits.unlimitedCreditsAddonPerYear', '/year')
                            : t('credits.unlimitedCreditsAddonPerMonth', '/month')}
                        </span>
                      </span>
                    </div>
                    <p
                      className="text-xs mt-1 mb-2"
                      style={{ color: 'var(--add-credits-modal-subscription-label)' }}
                    >
                      {t('credits.unlimitedCreditsAddonSubtitle', "For busy workspaces. We'll alert you at 80% and 100% of your monthly credits.")}
                    </p>
                    {unlimitedCreditsAddon.isActive ? (
                      <div
                        className="inline-flex items-center gap-1 text-xs font-medium"
                        style={{ color: 'var(--add-credits-modal-subscription-value)' }}
                      >
                        <Check className="h-3.5 w-3.5" />
                        {t('credits.unlimitedCreditsAddonActive', 'Active on your subscription')}
                      </div>
                    ) : (
                      <Button
                        variant="default"
                        size="sm"
                        className="h-8 text-xs px-4"
                        style={{
                          backgroundColor: isAddonLoading
                            ? 'var(--add-credits-modal-button-disabled-bg)'
                            : 'var(--add-credits-modal-button-primary-bg)',
                          color: isAddonLoading
                            ? 'var(--add-credits-modal-button-disabled-text)'
                            : 'var(--add-credits-modal-button-primary-text)'
                        }}
                        onClick={handleAddUnlimitedCredits}
                        disabled={isAddonLoading}
                        data-testid="unlimited-credits-addon-add"
                      >
                        {isAddonLoading ? (
                          <>
                            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                            {t('credits.processing', 'Processing...')}
                          </>
                        ) : (
                          t('credits.unlimitedCreditsAddonAdd', 'Add to subscription')
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Credit Packages Section */}
        <div className="hide-billing-cycle">
          <div
            className="text-sm font-medium px-2"
            style={{
              color: 'var(--add-credits-modal-section-title)',
              backgroundColor: 'var(--add-credits-modal-bg)'
            }}
          >
            {hasActiveSubscription ?
              t('credits.creditPackages', 'Credit packages') :
              t('credits.creditPackagesPreview', 'Available credit packages')}
          </div>
        </div>

        {creditPackages.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 my-2 packages-grid" data-hide-billing-cycle-text="true">
            {creditPackages.map((pkg) => {
              // Get pricing for current billing cycle
              const pricing = billingCycle === 'monthly' ? pkg.monthly : pkg.annual;

              // Skip if this package doesn't have pricing for the selected cycle
              if (!pricing) return null;

              const isSelected = selectedPackageId === pricing.id;

              // #143 option (b): one-time top-ups are REPEATABLE — any size can be bought
              // again, so there is no "already owned" disable/guard anymore.

              // Fixed popular logic - checking the package's popular property directly
              // Adding fallback to mark 200 credit package as popular if no package has popular=true
              const isRecommended = pkg.oneTime === true && recommendedCredits !== null && pkg.credits === recommendedCredits;
              const lastsDays = pkg.oneTime ? packLastsDays(pkg.credits, workspacePerDay) : null;
              const isPopular = !isRecommended && (pkg.popular === true || (creditPackages.every(p => p.popular !== true) && pkg.credits === 200));

              return (
                <div key={pricing.id} className="h-[104px]">
                  <Card
                    className={`
                      h-full w-full px-3 pt-1.5 pb-2 cursor-pointer relative border-2 shadow-sm rounded-lg overflow-visible box-border
                      ${isSelected ? 'shadow' : 'hover:shadow'}
                      ${!billingCycleMatchesSubscription ? 'opacity-70 pointer-events-none' : ''}
                    `}
                    style={{
                      transition: 'background-color 0.2s, border-color 0.2s',
                      position: 'relative',
                      zIndex: 1,
                      backgroundColor: isSelected
                        ? 'var(--add-credits-modal-package-card-selected-bg)'
                        : 'var(--add-credits-modal-package-card-bg)',
                      borderColor: isSelected
                        ? 'var(--add-credits-modal-package-card-selected-border)'
                        : 'var(--add-credits-modal-package-card-border)'
                    }}
                    onClick={() => handleSelectPackage(pkg)}
                  >
                    {isRecommended && (
                      <div
                        className="absolute -top-2 right-2 text-xs px-2 py-0.5 rounded-full font-medium shadow-sm z-[50]"
                        style={{
                          backgroundColor: 'var(--add-credits-modal-popular-badge-bg)',
                          color: 'var(--add-credits-modal-popular-badge-text)'
                        }}
                        data-testid="pack-recommended"
                      >
                        {t('credits.recommendedForYou', 'Recommended for you')}
                      </div>
                    )}
                    {isPopular && (
                      <div
                        className="absolute -top-2 right-2 text-xs px-2 py-0.5 rounded-full font-medium shadow-sm z-[50]"
                        style={{
                          backgroundColor: 'var(--add-credits-modal-popular-badge-bg)',
                          color: 'var(--add-credits-modal-popular-badge-text)'
                        }}
                      >
                        <span className="flex items-center">
                          <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.799-2.034c-.784-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                          </svg>
                          {t('credits.popular', 'Popular')}
                        </span>
                      </div>
                    )}

                    {/* Main content */}
                    <div className="flex flex-col justify-between h-full">
                      {/* Credits row */}
                      <div className="flex items-baseline">
                        <div
                          className="text-xl font-bold leading-tight"
                          style={{ color: 'var(--add-credits-modal-package-credits)' }}
                        >
                          {pkg.credits}
                        </div>
                        <div
                          className="text-xs ml-1"
                          style={{ color: 'var(--add-credits-modal-package-per-credit)' }}
                        >
                          {pkg.oneTime ?
                            t('credits.creditsOneTime', 'credits (one-time)') :
                            billingCycle === 'monthly' ?
                            t('credits.creditsPerMonth', 'credits per month') :
                            t('credits.creditsPerMonthAnnual', 'credits per month (annual plan)')}
                        </div>
                      </div>

                      {/* Price row */}
                      <div className="flex items-baseline">
                        <div
                          className="text-base font-medium leading-tight"
                          style={{ color: 'var(--add-credits-modal-package-price)' }}
                        >
                          ${pricing.price.toFixed(2)}
                        </div>
                        <div
                          className="text-xs ml-1"
                          style={{ color: 'var(--add-credits-modal-package-per-credit)' }}
                        >
                          (${pricing.perCredit.toFixed(3)}/{t('credits.perCreditLabel', 'credit')})
                        </div>
                      </div>

                      {/* Additional info */}
                      <div className="text-xs">
                        {lastsDays !== null && (
                          <div style={{ color: 'var(--add-credits-modal-package-description)' }} data-testid="pack-lasts">
                            {lastsDays < 1
                              ? t('credits.packLastsUnderDay', "Lasts under a day at your workspace's pace")
                              : t('credits.packLastsDays', { count: lastsDays, defaultValue_one: "Lasts ~1 day at your workspace's pace", defaultValue_other: "Lasts ~{{count}} days at your workspace's pace" })}
                          </div>
                        )}
                        {pkg.description && !pkg.description.includes(pkg.credits.toString()) && (
                          <div
                            className="line-clamp-1"
                            style={{ color: 'var(--add-credits-modal-package-description)' }}
                          >
                            {pkg.description}
                          </div>
                        )}

                        {pkg.savePercentage && (
                          <div style={{ color: 'var(--add-credits-modal-package-save)' }}>
                            {t('credits.savePercentage', 'Save {{percentage}}%', { percentage: pkg.savePercentage })}
                          </div>
                        )}
                      </div>
                    </div>

                    {isSelected && (
                      <div
                        className="absolute bottom-1.5 right-2"
                        style={{ color: 'var(--add-credits-modal-package-card-selected-border)' }}
                      >
                        <Check className="h-4 w-4" />
                      </div>
                    )}
                  </Card>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex justify-center items-center h-32 my-4">
            {isLoading ? (
              <p style={{ color: 'var(--add-credits-modal-loading-text)' }}>
                {t('credits.loading', 'Loading packages...')}
              </p>
            ) : (
              <p style={{ color: 'var(--add-credits-modal-loading-text)' }}>
                {t('credits.noPackages', 'No credit packages available')}
              </p>
            )}
          </div>
        )}



        {topUpPacks.length > 0 && (
          <p className="text-xs my-1" style={{ color: 'var(--add-credits-modal-package-description)' }} data-testid="topup-validity-note">
            {t('credits.topUpValidityNote', 'Top-up credits never expire. Monthly plan credits reset each billing period and do not carry over.')}
          </p>
        )}

        {upgradeOption && selectedTopUpPerCredit > 0 && (
          <div
            className="text-xs rounded-md border p-2 my-1"
            style={{
              borderColor: 'var(--add-credits-modal-subscription-border)',
              color: 'var(--add-credits-modal-package-description)'
            }}
            data-testid="topup-vs-upgrade"
          >
            <div className="font-medium" style={{ color: 'var(--add-credits-modal-section-title)' }}>
              {t('credits.compareTitle', 'Top-up or upgrade?')}
            </div>
            <div>
              {t('credits.compareTopUp', 'Top-up: ${{cost}} per credit, one-time', { cost: selectedTopUpPerCredit.toFixed(3) })}
            </div>
            <div>
              {t('credits.compareUpgrade', 'Upgrade to {{plan}}: +{{credits}} credits every month for ${{extra}} more (${{cost}} per extra credit)', {
                plan: upgradeOption.planName,
                credits: upgradeOption.extraCredits,
                extra: upgradeOption.extraPrice.toFixed(2),
                cost: upgradeOption.perExtraCredit.toFixed(3),
              })}
            </div>
          </div>
        )}

        <DialogFooter className="flex items-center justify-end space-x-3 mt-4">
          <DialogClose asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-10 text-sm px-4"
              style={{
                backgroundColor: 'var(--add-credits-modal-button-cancel-bg)',
                borderColor: 'var(--add-credits-modal-button-cancel-border)',
                color: 'var(--add-credits-modal-button-cancel-text)'
              }}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
          </DialogClose>

          {/* Different buttons based on subscription status */}
          {hasActiveSubscription ? (
            <Button
              variant="default"
              size="sm"
              className="h-10 text-sm px-5"
              style={{
                backgroundColor: isLoading || !selectedPackageId || !billingCycleMatchesSubscription
                  ? 'var(--add-credits-modal-button-disabled-bg)'
                  : 'var(--add-credits-modal-button-primary-bg)',
                color: isLoading || !selectedPackageId || !billingCycleMatchesSubscription
                  ? 'var(--add-credits-modal-button-disabled-text)'
                  : 'var(--add-credits-modal-button-primary-text)'
              }}
              onClick={handleContinueToCheckout}
              disabled={isLoading || !selectedPackageId || !billingCycleMatchesSubscription}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('credits.processing', 'Processing...')}
                </>
              ) : (
                t('credits.continueToCheckout', 'Continue to Checkout')
              )}
            </Button>
          ) : (
            <Button
              variant="default"
              size="sm"
              className="h-10 text-sm px-5"
              style={{
                backgroundColor: !selectedPlanId || isSubscriptionLoading
                  ? 'var(--add-credits-modal-button-disabled-bg)'
                  : 'var(--add-credits-modal-button-primary-bg)',
                color: !selectedPlanId || isSubscriptionLoading
                  ? 'var(--add-credits-modal-button-disabled-text)'
                  : 'var(--add-credits-modal-button-primary-text)'
              }}
              onClick={handleSubscriptionCheckout}
              disabled={!selectedPlanId || isSubscriptionLoading}
            >
              {isSubscriptionLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('credits.processing', 'Processing...')}
                </>
              ) : selectedPackageId ? (
                t('credits.subscribeWithCredits', 'Subscribe + Add Credits')
              ) : (
                t('credits.subscribeNow', 'Subscribe Now')
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddCreditsModal; 